import { useState } from "react";

import { Box, Button, Grid, Stack, Typography } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { RefreshPageButton } from "components/RefreshPageButton";
import {
  useDisclosure,
  useShipperNetworkGroups,
  useSubscriptionStatus,
} from "hooks";

import { ShipperUpgradeNowButton } from "../ShipperUpgradeNowButton";
import { CreateNetworkGroupModal } from "./components/CreateNetworkGroupModal";
import { NetworkGroupDetailModal } from "./components/NetworkGroupDetailModal";
import { NetworkGroupOverview } from "./components/NetworkGroupOverview";

export function NetworkGroupManager() {
  const {
    networkGroups,
    loadingNetworkGroups,
    networkGroupsError,
    refetchNetworkGroups,
  } = useShipperNetworkGroups();

  const {
    isOpen: manageGroupOpen,
    onClose: onCloseManageGroup,
    onOpen: onOpenManageGroup,
  } = useDisclosure(false);

  const {
    isOpen: createGroupOpen,
    onClose: onCloseCreateGroup,
    onOpen: onOpenCreateGroup,
  } = useDisclosure(false);

  const [selectedGroup, setSelectedGroup] = useState<NetworkGroup | null>(null);
  const { subscription } = useSubscriptionStatus();

  const onManageGroupSelected = (branch: NetworkGroup) => {
    setSelectedGroup(branch);
    onOpenManageGroup();
  };

  if (subscription == null) {
    return null;
  }
  const isFreeTier = subscription.subscription_plan === "SHIPPER_FREE_TIER";

  return (
    <Stack direction="column" mt="1rem" spacing={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="h6">Network Groups</Typography>
        <RefreshPageButton
          id="refresh-branches"
          onRefresh={refetchNetworkGroups}
          iconButton
        />
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          onClick={onOpenCreateGroup}
          disabled={isFreeTier}
        >
          Create Group
        </Button>
        {isFreeTier && (
          <ShipperUpgradeNowButton
            buttonSize="small"
            trackingSource="shipper:create_network_group_paywall"
          />
        )}
      </Stack>
      <LoadingWrappedNode loading={loadingNetworkGroups}>
        <Box mt="1rem">
          <Grid container spacing={2} alignItems="stretch">
            {networkGroups.map((group) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={group.group.id}>
                <NetworkGroupOverview
                  group={group}
                  onManageGroup={onManageGroupSelected}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </LoadingWrappedNode>
      <ErrorTypography error={networkGroupsError} />
      <NetworkGroupDetailModal
        isOpen={manageGroupOpen}
        onClose={onCloseManageGroup}
        onRefreshNetworkGroups={refetchNetworkGroups}
        group={selectedGroup}
      />
      <CreateNetworkGroupModal
        isOpen={createGroupOpen}
        onClose={onCloseCreateGroup}
        onRefreshNetworkGroups={refetchNetworkGroups}
      />
    </Stack>
  );
}
